import { useMemo } from "react";
import { Box, Button, Checkbox, Chip, Typography } from "@material-ui/core";
import { orderBy } from "lodash";
import DataTable from "components/DataTable/DataTable";
import useSortOptions from "hooks/useSortOptions";
import { PricingLotDetailsDialogStyles } from "./PricingLotDetailsDialogStyles";
import FormatService from "services/formatService";
import { useGetOtherVendorOptionsQuery } from "features/pricing/pricingSlice";
import LoadingSpinner from "components/LoadingSpinner";

const OtherVendorOptionsTab = ({ pricingData, vendorLookup, preferredVendorId, onPreferredVendorChange, isSelecting }) => {
    const classes = PricingLotDetailsDialogStyles();
    const hasQueryParams = Boolean(pricingData?.clientId && pricingData?.lotId && pricingData?.activityItemId);
    const { data: otherVendorOptions = [], isFetching: isLoadingOtherVendorOptions } = useGetOtherVendorOptionsQuery(
        {
            clientId: pricingData?.clientId,
            lotId: pricingData?.lotId,
            activityItemId: pricingData?.activityItemId,
        },
        {
            skip: !hasQueryParams,
        }
    );

    const vendorOptions = useMemo(() => {
        if (otherVendorOptions.length) {
            return otherVendorOptions.map((option) => ({
                ...option,
                vendorName: vendorLookup?.[option.vendorId]?.name || "Unknown Vendor",
                isPreferred: option?.vendorId == preferredVendorId,
            }));
        }
        return [];
    }, [otherVendorOptions, vendorLookup, preferredVendorId]);

    const [sortOptions, sortData] = useSortOptions();

    if (!pricingData && !isLoadingOtherVendorOptions) {
        return <Typography className={classes.emptyMessage}>No vendor options available.</Typography>;
    }

    const columns = [
        {
            name: "Vendor",
            key: "vendorName",
            width: 260,
            component: (row) => {
                return (
                    <Box className={classes.vendorNameWrapper}>
                        <span>{row.vendorName}</span>
                        <Chip
                            size="small"
                            label={row.isPreferred ? "Preferred" : "Alternative"}
                            className={row.isPreferred ? classes.preferredChip : classes.alternativeChip}
                        />
                    </Box>
                );
            },
        },
        {
            name: "Vendor Cost",
            key: "vendorCost",
            width: 120,
            component: (row) => FormatService.formatCurrency(row.vendorCost) || "-",
        },
        {
            name: "Margin",
            key: "margin",
            width: 100,
            component: (row) => FormatService.formatPercent(row.margin),
        },
        {
            name: "Client Charge",
            key: "clientCharge",
            width: 120,
            component: (row) => FormatService.formatCurrency(row.clientCharge) || "-",
        },
        {
            name: "Action",
            key: "action",
            width: 110,
            component: (row) => {
                if (row.isPreferred) {
                    return (
                        <Box className={classes.activeAction}>
                            <Checkbox
                                checked={true}
                                disabled
                                color="primary"
                                size="small"
                            />
                            <Typography className={classes.activeLabel}>Active</Typography>
                        </Box>
                    );
                }
                return (
                    <Button
                        size="small"
                        variant="outlined"
                        className={classes.selectButton}
                        disabled={isSelecting}
                        onClick={() => onPreferredVendorChange(row.vendorId)}
                    >
                        Select
                    </Button>
                );
            },
        },
    ];

    return (
        <Box className={classes.vendorOptionsTableWrapper}>
            <LoadingSpinner loading={isLoadingOtherVendorOptions} />
            <DataTable
                columns={columns}
                rows={orderBy(vendorOptions, sortOptions.columnToSort, sortOptions.sortDirection)}
                rowIdentifier="vendorId"
                noItemsMessage="No alternative vendor options available for this activity and lot."
                allowHorizontalScroll={false}
                className={classes.vendorOptionsTable}
                onSort={sortData}
                sortDirection={sortOptions.sortDirection}
                columnToSort={sortOptions.columnToSort}
            />
        </Box>
    );
};

export default OtherVendorOptionsTab;
